import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { generateClient } from 'aws-amplify/api';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import { listUserProfiles } from '../graphql/queries';
import { createUserProfile, updateUserProfile } from '../graphql/mutations';
import Questionnaire from '../components/features/Questionnaire/Questionnaire'; // Adjust path to match your component

const UserPage = () => {
  const [editMode, setEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    email: '',
    username: '',
    yearOfStudy: '',
    medicalUniversity: '',
    demographics: '',
    chosenSpecialty: '',
    specialtyUpdatedAt: null
  });
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);

  // Create API client
  // memoize client so its identity is stable across renders
  const client = useMemo(() => generateClient(), []);

  // Define fetchUserProfile with useCallback
  const fetchUserProfile = useCallback(async (user) => {
    try {
      if (!user) {
        console.error('No user provided to fetchUserProfile');
        return;
      }

      const { data } = await client.graphql({
        query: listUserProfiles,
        variables: {
          filter: { owner: { eq: user.username } }
        },
        authMode: 'userPool'
      });

      if (data.listUserProfiles.items.length > 0) {
        const profile = data.listUserProfiles.items[0];
        setFormData({
          id: profile.id,
          name: profile.name || '',
          email: profile.email || (user.attributes?.email || ''),
          username: user.username || '',
          yearOfStudy: profile.yearInMedSchool || '',
          medicalUniversity: profile.medicalUniversity || '',
          demographics: profile.demographics || '',
          chosenSpecialty: profile.chosenSpecialty || '',
          specialtyUpdatedAt: profile.specialtyUpdatedAt || null
        });
      } else {
        // If no profile exists, set default values
        setFormData(prev => ({
          ...prev,
          email: user.attributes?.email || '',
          username: user.username || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  }, [client]);

  // Define fetchCurrentUser with useCallback
  const fetchCurrentUser = useCallback(async () => {
    try {
      const user = await getCurrentUser();
      if (!user) {
        console.error('No user found');
        return;
      }

      const attributes = await fetchUserAttributes();
      const userWithAttributes = {
        ...user,
        attributes: attributes || {}
      };
      
      setCurrentUser(userWithAttributes);
      await fetchUserProfile(userWithAttributes);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  }, [fetchUserProfile]);

  // Fetch current authenticated user and their profile data
  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const saveChanges = async () => {
    try {
      console.log('Starting save with formData:', formData);

      // Validate required fields
      if (!formData.name || !formData.email || !formData.username) {
        alert('Please fill in all required fields (Name, Email, Username)');
        return;
      }

      const yearInMedSchool = parseInt(formData.yearOfStudy);
      if (isNaN(yearInMedSchool)) {
        alert('Year of Study must be a valid number');
        return;
      }

      // First check if profile exists with same email
      const { data: existingProfiles } = await client.graphql({
        query: listUserProfiles,
        variables: {
          filter: { 
            email: { eq: formData.email }
          }
        },
        authMode: 'userPool'
      });


      console.log('Existing profiles:', existingProfiles);

      const input = {
        name: formData.name,
        email: formData.email,
        username: formData.username,
        yearInMedSchool: yearInMedSchool,
        medicalUniversity: formData.medicalUniversity || '',
        demographics: formData.demographics || '',
        owner: currentUser.username
      };

      if (existingProfiles.listUserProfiles.items.length > 0) {
        // Update existing profile
        const existingProfile = existingProfiles.listUserProfiles.items[0];
        const result = await client.graphql({
          query: updateUserProfile,
          variables: { 
            input: {
              id: existingProfile.id,
              ...input
            }
          },
          authMode: 'userPool'
        });
        console.log('Update result:', result);
      } else {
        // Create new profile only if one doesn't exist
        const result = await client.graphql({
          query: createUserProfile,
          variables: { input },
          authMode: 'userPool'
        });
        console.log('Create result:', result);
      }

      setEditMode(false);
      await fetchUserProfile(currentUser);
      alert('Profile saved successfully!');
    } catch (error) {
      console.error('Full error object:', error);
      alert(`Error saving profile: ${error.message || 'Unknown error occurred'}`);
    }
  };

  const toggleQuestionnaire = () => {
    setShowQuestionnaire(!showQuestionnaire);
  };

  return (
    <div className="content">
      {/* Hero */}
      <section className="up-hero">
        <div className="up-inner">
          <header className="up-hero-head">
            <h1 className="up-title">
              Your <span className="up-gradient">MedMap Profile</span>
            </h1>
            <p className="up-sub">
              Keep your details up-to-date and track your specialty journey with a clean, modern profile.
            </p>

            <div className="up-hero-stats">
              <div className="up-stat">
                <span className="up-stat-label">Signed in</span>
                <span className="up-stat-value">{formData.email || '—'}</span>
              </div>
              <div className="up-divider" />
              <div className="up-stat">
                <span className="up-stat-label">Goal specialty</span>
                <span className="up-stat-value">{formData.chosenSpecialty || 'Not set'}</span>
              </div>
            </div>

            <div className="up-cta-cluster">
              {editMode ? (
                <button onClick={saveChanges} className="up-cta-primary">
                  Save changes
                </button>
              ) : (
                <button onClick={() => setEditMode(true)} className="up-cta-primary">
                  Edit profile
                </button>
              )}

              <button onClick={toggleQuestionnaire} className="up-cta-secondary">
                {showQuestionnaire ? 'Hide questionnaire' : 'Show questionnaire'}
              </button>

              {/* New: Manage subscriptions button (same visual style as secondary CTA) */}
              <a
                href="/billing"
                className="up-cta-secondary"
                aria-label="Manage subscriptions"
                style={{ textDecoration: 'none' }}
              >
                Manage subscriptions
              </a>
            </div>
          </header>
        </div>
      </section>

      {/* Cards grid */}
      <div className="up-inner">
        <div className="up-grid">
          {/* General Information */}
          <article className="up-card">
            <div className="up-card-head">
              <h2>General Information</h2>
            </div>
            <div className="up-card-body">
              {editMode ? (
                <>
                  <label className="up-field">
                    <span>Name</span>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                    />
                  </label>
                  <label className="up-field">
                    <span>Email</span>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </label>
                  <label className="up-field">
                    <span>Username</span>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                    />
                  </label>
                </>
              ) : (
                <>
                  <p><strong>Name:</strong> {formData.name || '—'}</p>
                  <p><strong>Email:</strong> {formData.email || '—'}</p>
                  <p><strong>Username:</strong> {formData.username || '—'}</p>
                </>
              )}
            </div>
          </article>

          {/* Academic Information */}
          <article className="up-card">
            <div className="up-card-head">
              <h2>Academic Information</h2>
            </div>
            <div className="up-card-body">
              {editMode ? (
                <>
                  <label className="up-field">
                    <span>Year of Study</span>
                    <input
                      type="text"
                      name="yearOfStudy"
                      value={formData.yearOfStudy}
                      onChange={handleInputChange}
                    />
                  </label>
                  <label className="up-field">
                    <span>Medical University</span>
                    <input
                      type="text"
                      name="medicalUniversity"
                      value={formData.medicalUniversity}
                      onChange={handleInputChange}
                    />
                  </label>
                </>
              ) : (
                <>
                  <p><strong>Year of Study:</strong> {formData.yearOfStudy || '—'}</p>
                  <p><strong>Medical University:</strong> {formData.medicalUniversity || '—'}</p>
                </>
              )}
            </div>
          </article>

          {/* Demographics */}
          <article className="up-card">
            <div className="up-card-head">
              <h2>Demographics</h2>
            </div>
            <div className="up-card-body">
              {editMode ? (
                <label className="up-field">
                  <span>Demographics</span>
                  <input
                    type="text"
                    name="demographics"
                    value={formData.demographics}
                    onChange={handleInputChange}
                  />
                </label>
              ) : (
                <p><strong>Demographics:</strong> {formData.demographics || '—'}</p>
              )}
            </div>
          </article>

          {/* Career Goal */}
          <article className="up-card">
            <div className="up-card-head">
              <h2>Career Goal</h2>
            </div>
            <div className="up-card-body">
              {formData.chosenSpecialty ? (
                <>
                  <p><strong>Chosen Specialty:</strong> {formData.chosenSpecialty}</p>
                  <p><strong>Last Updated:</strong> {
                    formData.specialtyUpdatedAt
                      ? new Date(formData.specialtyUpdatedAt).toLocaleDateString()
                      : 'Not set'
                  }</p>
                </>
              ) : (
                <p>No specialty chosen yet. Visit the Dream Specialty page to set your goal.</p>
              )}
            </div>
          </article>
        </div>

        {/* Questionnaire */}
        {showQuestionnaire && (
          <article className="up-card">
            <div className="up-card-head">
              <h2>Questionnaire</h2>
            </div>
            <div className="up-card-body">
              <Questionnaire userID={formData.id} />
            </div>
          </article>
        )}
      </div>

      {/* Scoped styles for User Page */}
      <style jsx>{`
        :root {
          --up-max: 1220px;
          --up-accent: #2563eb;
          --up-accent-alt: #3b82f6;
          --up-accent-alt2: #6366f1;
          --up-gray-50: #f8fafc;
          --up-gray-100: #f1f5f9;
          --up-gray-200: #e2e8f0;
          --up-gray-300: #cbd5e1;
          --up-gray-600: #475569;
          --up-gray-700: #0f172a;
          --up-radius-lg: 20px;
          --up-radius-pill: 999px;
          --up-shadow-sm: 0 4px 14px -6px rgba(15,23,42,.08);
          --up-shadow-hover: 0 18px 42px -16px rgba(15,23,42,.25);
          --up-gradient-text: linear-gradient(90deg,#2563eb,#3b82f6,#6366f1);
          --up-gradient-card: linear-gradient(135deg,#2563eb,#3b82f6 55%,#6366f1);
        }

        .up-inner {
          width: min(92%, var(--up-max));
          margin: 0 auto;
        }

        .up-hero {
          background: radial-gradient(circle at 25% 18%, #eef6ff 0%, #ffffff 68%);
          padding: clamp(4.5rem, 10vh, 7rem) 1.75rem 2.25rem;
          border-bottom: 1px solid var(--up-gray-200);
        }

        .up-hero-head {
          text-align: center;
          max-width: 940px;
          margin: 0 auto;
        }

        .up-title {
          margin: 0 0 0.9rem;
          font-size: clamp(2.1rem, 4vw, 3rem);
          font-weight: 700;
          letter-spacing: -0.03em;
          color: var(--up-gray-700);
        }

        .up-gradient {
          background: var(--up-gradient-text);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }

        .up-sub {
          margin: 0 auto 1.5rem;
          font-size: clamp(.98rem,1.05vw,1.1rem);
          line-height: 1.55;
          color: var(--up-gray-600);
          max-width: 62ch;
        }

        .up-hero-stats {
          display: inline-flex;
          align-items: center;
          gap: 1rem;
          background: #fff;
          border: 1px solid var(--up-gray-200);
          border-radius: var(--up-radius-pill);
          padding: .6rem .9rem;
          box-shadow: var(--up-shadow-sm);
          margin: .5rem auto 1.25rem;
        }

        .up-stat {
          display: grid;
          gap: .2rem;
          text-align: left;
        }
        .up-stat-label {
          font-size: .72rem;
          color: var(--up-gray-600);
        }
        .up-stat-value {
          font-size: .9rem;
          font-weight: 600;
          color: var(--up-gray-700);
        }
        .up-divider {
          width: 1px;
          height: 28px;
          background: var(--up-gray-200);
        }

        .up-cta-cluster {
          display: flex;
          gap: .85rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .up-cta-primary,
        .up-cta-secondary {
          display: inline-flex;
          align-items: center;
          gap: .55rem;
          padding: .9rem 1.55rem;
          border-radius: var(--up-radius-pill);
          font-weight: 700;
          font-size: .95rem;
          text-decoration: none;
          cursor: pointer;
          transition: all .45s cubic-bezier(.34,1.56,.64,1);
          border: 1px solid transparent;
        }
        .up-cta-primary {
          background: var(--up-gradient-card);
          color: #fff;
          box-shadow: var(--up-shadow-sm);
        }
        .up-cta-primary:hover {
          transform: translateY(-3px);
          box-shadow: var(--up-shadow-hover);
        }
        .up-cta-secondary {
          background: #fff;
          color: var(--up-gray-700);
          border-color: var(--up-gray-200);
        }
        .up-cta-secondary:hover {
          background: var(--up-gray-50);
          transform: translateY(-2px);
          box-shadow: 0 10px 26px -10px rgba(15,23,42,.15);
        }

        .up-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.25rem;
          padding: 1.75rem;
        }
        @media (max-width: 920px) {
          .up-grid { grid-template-columns: 1fr; }
        }

        .up-card {
          background: #fff;
          border: 1px solid var(--up-gray-200);
          border-radius: var(--up-radius-lg);
          padding: 1.4rem 1.25rem 1.25rem;
          box-shadow: var(--up-shadow-sm);
          transition: transform .55s cubic-bezier(.34,1.56,.64,1), box-shadow .55s;
        }
        .up-card:hover {
          transform: translateY(-6px);
          box-shadow: var(--up-shadow-hover);
        }

        .up-card-head {
          border-bottom: 1px solid var(--up-gray-200);
          padding-bottom: .9rem;
          margin-bottom: 1rem;
        }
        .up-card-head h2 {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--up-gray-700);
          letter-spacing: -.02em;
        }

        .up-card-body {
          display: grid;
          gap: .85rem;
        }

        .up-field {
          display: grid;
          gap: .4rem;
        }
        .up-field span {
          font-size: .85rem;
          color: var(--up-gray-600);
          font-weight: 600;
        }
        .up-field input {
          padding: .75rem .9rem;
          border: 1px solid var(--up-gray-200);
          border-radius: 12px;
          background: #fff;
          color: var(--up-gray-700);
          font-size: .95rem;
          outline: none;
          transition: border-color .25s, box-shadow .25s;
        }
        .up-field input:focus {
          border-color: var(--up-accent);
          box-shadow: 0 0 0 3px rgba(37,99,235,.15);
        }

        /* @media (prefers-color-scheme: dark) {
          .up-hero { background: linear-gradient(180deg,#0f172a,#1e293b); }
          .up-title { color:#f1f5f9; }
          .up-sub { color:#94a3b8; }
          .up-hero-stats { background:#1e293b; border-color:#334155; }
          .up-stat-value { color:#e2e8f0; }
          .up-card { background:#1e293b; border-color:#334155; }
          .up-field input { background:#0f172a; border-color:#334155; color:#e2e8f0; }
          .up-cta-secondary { background:#1e293b; color:#e2e8f0; border-color:#334155; }
          .up-cta-secondary:hover { background:#2d3b52; }
        } */

        /* Focus states */
        .up-cta-primary:focus-visible,
        .up-cta-secondary:focus-visible,
        .up-field input:focus-visible {
          outline: 3px solid #93c5fd;
          outline-offset: 3px;
        }
      `}</style>
    </div>
  );
};

export default UserPage;
